// import React from 'react';
import { MapPin, Clock, DollarSign, Users, ArrowRight, Briefcase, Heart, Target } from 'lucide-react';
import { Button } from '../ui/button';

interface CareersPageProps {
  onNavigate: (page: string) => void;
}

export function CareersPage({ onNavigate }: CareersPageProps) {
  const benefits = [
    {
      icon: DollarSign,
      title: 'Competitive Compensation',
      description: 'Market-leading salaries, equity options, and performance bonuses',
    },
    {
      icon: MapPin,
      title: 'Remote-First Culture',
      description: 'Work from anywhere in the US/Canada with flexible hours',
    },
    {
      icon: Users,
      title: 'Health & Wellness',
      description: 'Comprehensive health, dental, vision, and mental health coverage',
    },
    {
      icon: Clock,
      title: 'Time Off',
      description: 'Unlimited PTO, paid holidays, and company-wide rest weeks',
    },
  ];

  const perks = [
    'Professional development budget',
    'Home office stipend',
    'Latest equipment (MacBook, monitors, etc.)',
    '401(k) with company match',
    'Parental leave',
    'Team retreats and events',
    'Learning & conference budget',
    'Wellness programs',
  ];

  const culture = [
    {
      icon: Target,
      title: 'Mission-Driven',
      description: 'Build technology that transforms how enterprises deliver projects and create value',
    },
    {
      icon: Heart,
      title: 'People-First',
      description: 'We believe in work-life balance, continuous learning, and supporting each other',
    },
    {
      icon: Users,
      title: 'Collaborative',
      description: 'Cross-functional teams working together to solve complex problems',
    },
  ];

  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#2A3B4C] via-[#3E5365] to-[#556B7C] text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-20 w-64 h-64 bg-[#DAA520] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-[#3E5365] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h1 className="text-5xl mb-6">Join the Karyva Team</h1>
              <p className="text-xl text-[#D5D9DD] leading-relaxed mb-8">
                Help us transform enterprise project portfolio management. Build innovative solutions,
                work with cutting-edge technology, and make a real impact on how organizations deliver projects.
              </p>
              <div className="flex flex-wrap items-center gap-6 text-[#D5D9DD]">
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-[#DAA520]" />
                  <span>Remote-First</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-[#DAA520]" />
                  <span>Growing Team</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart className="w-5 h-5 text-[#DAA520]" />
                  <span>People-Focused Culture</span>
                </div>
              </div>
            </div>
            <div className="flex items-center justify-center">
              <div className="relative">
                <div className="absolute inset-0 bg-[#DAA520] opacity-20 blur-3xl rounded-full"></div>
                <img 
                  src="https://images.unsplash.com/photo-1758630737900-a28682c5aa69?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBvZmZpY2UlMjBpbnRlcmlvcnxlbnwxfHx8fDE3NjcxMDY5NDR8MA&ixlib=rb-4.1.0&q=80&w=1080"
                  alt="Office Environment"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Culture */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Our Culture</h2>
            <p className="text-xl text-[#556B7C] max-w-3xl mx-auto">
              Join a team that values innovation, collaboration, and work-life balance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {culture.map((item, idx) => {
              const Icon = item.icon;
              return (
                <div key={idx} className="bg-[#E8E9E9] rounded-lg p-8 border border-[#C4CDD5] text-center">
                  <div className="w-14 h-14 bg-[#DAA520] rounded-full flex items-center justify-center mx-auto mb-4">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl text-[#2A3B4C] mb-3">{item.title}</h3>
                  <p className="text-[#556B7C] leading-relaxed">{item.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Benefits & Perks</h2>
            <p className="text-xl text-[#556B7C]">
              We invest in our team's success and well-being
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 mb-12">
            {benefits.map((benefit, idx) => {
              const Icon = benefit.icon;
              return (
                <div key={idx} className="bg-white rounded-lg p-6 border border-[#C4CDD5]">
                  <div className="w-12 h-12 bg-[#DAA520] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-lg text-[#2A3B4C] mb-2">{benefit.title}</h3>
                  <p className="text-sm text-[#556B7C]">{benefit.description}</p>
                </div>
              );
            })}
          </div>

          <div className="bg-white rounded-lg p-8 border border-[#C4CDD5]">
            <h3 className="text-xl text-[#2A3B4C] mb-6">Additional Perks</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
              {perks.map((perk, idx) => (
                <div key={idx} className="flex items-center gap-2 text-[#556B7C]">
                  <div className="w-5 h-5 bg-[#DAA520] rounded flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs">✓</span>
                  </div>
                  <span className="text-sm">{perk}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl text-[#2A3B4C] mb-4">Current Opportunities</h2>
          </div>

          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#E8E9E9] to-[#F5F5F5] rounded-lg border-2 border-dashed border-[#C4CDD5] p-12 text-center">
              <div className="w-20 h-20 bg-[#DAA520]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Briefcase className="w-10 h-10 text-[#DAA520]" />
              </div>
              <h3 className="text-2xl text-[#2A3B4C] mb-4">No Open Positions at This Time</h3>
              <p className="text-lg text-[#556B7C] mb-8 max-w-2xl mx-auto leading-relaxed">
                We're not actively hiring right now, but we're always interested in meeting talented individuals 
                who are passionate about enterprise project management and building world-class software.
              </p>
              <div className="bg-white rounded-lg p-6 mb-8 max-w-2xl mx-auto text-left">
                <h4 className="text-lg text-[#2A3B4C] mb-3">Interested in Future Opportunities?</h4>
                <p className="text-[#556B7C] mb-4">
                  We'd love to hear from you! Send us your resume and a note about what you're passionate about:
                </p>
                <a 
                  href="mailto:careers@karyva.com" 
                  className="text-[#DAA520] hover:text-[#B8860B] flex items-center gap-2"
                >
                  <span>careers@karyva.com</span>
                  <ArrowRight className="w-4 h-4" />
                </a>
              </div>
              <p className="text-sm text-[#556B7C]">
                We'll keep your information on file and reach out when relevant positions become available.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Join Karyva */}
      <section className="py-20 bg-[#E8E9E9]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl text-[#2A3B4C] mb-6">Why Join Karyva?</h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#DAA520] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white">1</span>
                  </div>
                  <div>
                    <h4 className="text-lg text-[#2A3B4C] mb-2">Impact at Scale</h4>
                    <p className="text-[#556B7C]">
                      Your work directly influences how Fortune 1000 companies manage billions of dollars in project portfolios
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#DAA520] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white">2</span>
                  </div>
                  <div>
                    <h4 className="text-lg text-[#2A3B4C] mb-2">Cutting-Edge Technology</h4>
                    <p className="text-[#556B7C]">
                      Work with modern tech stack, AI/ML capabilities, and enterprise cloud infrastructure
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#DAA520] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white">3</span>
                  </div>
                  <div>
                    <h4 className="text-lg text-[#2A3B4C] mb-2">Growth & Learning</h4>
                    <p className="text-[#556B7C]">
                      Continuous learning opportunities, professional development budget, and mentorship
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-[#DAA520] rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-white">4</span>
                  </div>
                  <div>
                    <h4 className="text-lg text-[#2A3B4C] mb-2">Flexible Work</h4>
                    <p className="text-[#556B7C]">
                      Remote-first culture with flexible hours and true work-life balance
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1709715357520-5e1047a2b691?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMG1lZXRpbmd8ZW58MXx8fHwxNzY3MTQxNDA1fDA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Business Meeting"
                className="rounded-lg shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl text-[#2A3B4C] mb-6">
            Let's Start a Conversation
          </h2>
          <p className="text-xl text-[#556B7C] mb-8">
            Even if we don't have open positions right now, we'd love to connect with talented people interested in our mission
          </p>
          <Button
            onClick={() => onNavigate('contact')}
            className="bg-[#DAA520] hover:bg-[#B8860B] text-white px-8 py-6 text-lg"
          >
            Get in Touch
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}